import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

// Pacific Time zone identifier
const PACIFIC_TIMEZONE = 'America/Los_Angeles';

export function formatDate(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    timeZone: PACIFIC_TIMEZONE,
  }).format(d);
}

export function formatTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: PACIFIC_TIMEZONE,
  }).format(d);
}

export function formatDateTime(date: string | Date): string {
  return `${formatDate(date)} at ${formatTime(date)}`;
}

// Format time with PT label for session displays
export function formatTimePT(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const time = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: PACIFIC_TIMEZONE,
  }).format(d);
  return `${time} PT`;
}

// Format time range with PT label (e.g., "9:00 AM - 10:30 AM PT")
export function formatTimeRangePT(start: string | Date, end: string | Date): string {
  const startDate = typeof start === 'string' ? new Date(start) : start;
  const endDate = typeof end === 'string' ? new Date(end) : end;
  const formatter = new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
    timeZone: PACIFIC_TIMEZONE,
  });
  return `${formatter.format(startDate)} - ${formatter.format(endDate)} PT`;
}

// Format date in Pacific timezone
export function formatDatePT(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    timeZone: PACIFIC_TIMEZONE,
  }).format(d);
}

// Format full date in Pacific timezone (e.g., "Wednesday, January 28")
export function formatFullDatePT(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-US', {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    timeZone: PACIFIC_TIMEZONE,
  }).format(d);
}

// Get ISO date string in Pacific timezone (e.g., "2026-01-28")
export function getDateStringPT(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('en-CA', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    timeZone: PACIFIC_TIMEZONE,
  }).format(d);
}

// Build the day list for an event's date range, in Pacific Time.
// Each entry's `date` is a YYYY-MM-DD string that matches getDateStringPT
// (so it can be compared directly against a session's start time).
export function getEventDays(
  dateStart: string | Date | undefined | null,
  dateEnd: string | Date | undefined | null
): { label: string; date: string }[] {
  if (!dateStart || !dateEnd) return [];
  const startStr = getDateStringPT(dateStart);
  const endStr = getDateStringPT(dateEnd);

  const startParts = startStr.split('-').map(Number);
  const endParts = endStr.split('-').map(Number);
  const startY = startParts[0];
  const startM = startParts[1];
  const startD = startParts[2];
  const endY = endParts[0];
  const endM = endParts[1];
  const endD = endParts[2];
  if (!startY || !startM || !startD || !endY || !endM || !endD) return [];

  const days: { label: string; date: string }[] = [];
  // Use a UTC Date as an arithmetic cursor — we never read its local fields,
  // only its UTC fields, so this is timezone-safe regardless of the browser TZ.
  const cursor = new Date(Date.UTC(startY, startM - 1, startD));
  const last = Date.UTC(endY, endM - 1, endD);
  let dayNum = 1;
  while (cursor.getTime() <= last) {
    const y = cursor.getUTCFullYear();
    const m = String(cursor.getUTCMonth() + 1).padStart(2, '0');
    const d = String(cursor.getUTCDate()).padStart(2, '0');
    const monthDay = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(cursor);
    days.push({ label: `Day ${dayNum} — ${monthDay}`, date: `${y}-${m}-${d}` });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
    dayNum++;
  }
  return days;
}

// Build the day list from a set of session start times, in Pacific Time.
// Resilient to events with bad date metadata (e.g. dateStart stored as
// midnight UTC, which slides back to the previous PT day): buckets always
// reflect the days sessions actually occur on.
export function getEventDaysFromSessions(
  starts: ReadonlyArray<string | Date>
): { label: string; date: string }[] {
  const unique = Array.from(new Set(starts.map((s) => getDateStringPT(s)))).sort();
  return unique.map((date, i) => {
    const parts = date.split('-').map(Number);
    const y = parts[0]!;
    const m = parts[1]!;
    const d = parts[2]!;
    const monthDay = new Intl.DateTimeFormat('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    }).format(new Date(Date.UTC(y, m - 1, d)));
    return { label: `Day ${i + 1} — ${monthDay}`, date };
  });
}

// Convert ISO timestamp to datetime-local format string in Pacific Time
// Used by admin forms so datetime-local inputs always show Pacific wall-clock time
export function toPacificDatetimeString(iso: string): string {
  const d = new Date(iso);
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIMEZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(d);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    parts.find((p) => p.type === type)?.value ?? '';
  return `${get('year')}-${get('month')}-${get('day')}T${get('hour')}:${get('minute')}`;
}

// Event-day bounds for admin datetime-local inputs.
// Returns the calendar date COMPONENT of the ISO timestamp (UTC slice) as a
// datetime-local string at 00:00. Using the UTC date — not the PT date — keeps
// the picker bounds aligned with the *intended* event day regardless of
// whether the event was stored at midnight UTC (which slides back to the
// previous PT day) or at the real PT start hour.
export function getEventDayStartLocal(iso: string): string {
  return `${new Date(iso).toISOString().slice(0, 10)}T00:00`;
}

// Mirror of getEventDayStartLocal at 23:59 — used as the `max` bound so the
// picker accepts any time on the event's last day.
export function getEventDayEndLocal(iso: string): string {
  return `${new Date(iso).toISOString().slice(0, 10)}T23:59`;
}

// Convert datetime-local string (interpreted as Pacific wall-clock time) to ISO/UTC
// Inverse of toPacificDatetimeString — used when admin submits a session form
export function pacificDatetimeToISO(datetimeLocal: string): string {
  const segments = datetimeLocal.split('T');
  const dateNums = (segments[0] ?? '').split('-').map(Number);
  const timeNums = (segments[1] ?? '').split(':').map(Number);
  const year = dateNums[0] ?? 0;
  const month = dateNums[1] ?? 1;
  const day = dateNums[2] ?? 1;
  const hour = timeNums[0] ?? 0;
  const minute = timeNums[1] ?? 0;

  // Create a UTC instant where the UTC clock reads the target wall-clock values
  const utcGuess = Date.UTC(year, month - 1, day, hour, minute);

  // Project that instant into Pacific to find the offset
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: PACIFIC_TIMEZONE,
    hourCycle: 'h23',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).formatToParts(new Date(utcGuess));
  const getNum = (type: Intl.DateTimeFormatPartTypes) =>
    parseInt(parts.find((p) => p.type === type)?.value ?? '0');

  const pacificAsUtc = Date.UTC(
    getNum('year'),
    getNum('month') - 1,
    getNum('day'),
    getNum('hour'),
    getNum('minute')
  );

  // offset = pacificAsUtc - utcGuess; targetUTC = utcGuess - offset
  return new Date(utcGuess - (pacificAsUtc - utcGuess)).toISOString();
}

export function formatRelativeTime(date: string | Date): string {
  const d = typeof date === 'string' ? new Date(date) : date;
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (seconds < 60) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return formatDate(date);
}

export function truncate(str: string, length: number): string {
  if (str.length <= length) return str;
  return str.slice(0, length) + '...';
}

export function displayName(user: { firstName: string; lastName: string }): string {
  return `${user.firstName} ${user.lastName}`.trim();
}

export function getInitials(name: string): string {
  return name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
}

export function debounce<T extends (...args: unknown[]) => unknown>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout | null = null;
  return (...args: Parameters<T>) => {
    if (timeout) clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

export function throttle<T extends (...args: unknown[]) => unknown>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

export function generateId(): string {
  return crypto.randomUUID();
}

export function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function normalizeUrl(url: string): string {
  const trimmed = url.trim();
  if (!trimmed) return '';
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

/** True when `value` parses as a URL. Mirrors the backend's Zod `.url()` check. */
export function isValidUrl(value: string): boolean {
  try {
    new URL(value);
    return true;
  } catch {
    return false;
  }
}
